import React, { useState, useMemo, useEffect } from "react";
import { dataCategories } from "../../config/categories";
import { useCategoryStats } from "../../hooks/useCategoryIndicators";
import { getIndicatorsForCategory } from "../../services/dataService";
import SearchBar from "../common/SearchBar";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

const CategoryGrid = ({
  onCategorySelect,
  selectedCategory,
  onSearchSelect,
}) => {
  const [expanded, setExpanded] = useState(null);
  const { stats, loading } = useCategoryStats();
  const [categorySamples, setCategorySamples] = useState({});

  // Load actual sample indicators from manifest for each category
  useEffect(() => {
    const loadSamples = async () => {
      const samples = {};
      for (const category of dataCategories) {
        try {
          const indicators = await getIndicatorsForCategory(category.id);
          samples[category.id] = indicators.slice(0, 5);
        } catch (error) {
          console.warn(`Failed to load samples for ${category.id}`, error);
          samples[category.id] = [];
        }
      }
      setCategorySamples(samples);
    };
    loadSamples();
  }, []);

  // Merge hardcoded category metadata with dynamic counts from manifest
  const categoriesWithCounts = useMemo(() => {
    if (loading) return dataCategories;

    return dataCategories.map((category) => {
      const stat = stats.find((s) => s.categoryId === category.id);
      const samples = categorySamples[category.id] || [];
      return {
        ...category,
        actualCount: stat ? stat.count : 0,
        sampleIndicators: samples,
      };
    });
  }, [stats, loading, categorySamples]);

  return (
    <div className="py-12">
      {/* Hero Section */}
      <div className="text-center mb-12 space-y-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
          India's Development Data
          <span className="block text-trac-600 text-xl md:text-2xl mt-2 font-semibold">
            One Stop Clearing House
          </span>
        </h2>
        <p className="text-gray-500 max-w-2xl mx-auto text-lg">
          Explore comprehensive indicators across health, education,
          demographics, and socio-economic parameters.
        </p>

        {/* Smart Search with Autocomplete */}
        <div className="max-w-2xl mx-auto pt-6 px-4">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-trac-200 to-sky-200 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative">
              <SearchBar
                onSelectIndicator={onSearchSelect}
                className="w-full shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 px-4">
        {categoriesWithCounts.map((category) => {
          const isSelected = selectedCategory === category.id;
          const displayIndicators =
            expanded === category.id
              ? category.sampleIndicators
              : (category.sampleIndicators || []).slice(0, 3);

          return (
            <div
              key={category.id}
              onClick={() => onCategorySelect(category.id)}
              className={`group relative flex flex-col h-full bg-white rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden
                ${
                  isSelected
                    ? "ring-2 ring-trac-500 shadow-xl"
                    : "shadow-sm hover:shadow-xl hover:-translate-y-1 border border-gray-100"
                }`}
            >
              {/* Card Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`flex items-center justify-center h-14 w-14 rounded-xl text-2xl text-white shadow-sm ${category.color} transition-transform group-hover:scale-110 duration-300`}
                  >
                    {category.icon}
                  </div>
                  <div className="px-3 py-1 bg-gray-50 rounded-full border border-gray-100">
                    <span className="text-xs font-semibold text-gray-500">
                      {loading ? "..." : category.actualCount} Indicators
                    </span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-trac-700 transition-colors">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
                  {category.description}
                </p>
              </div>

              {/* Divider */}
              <div className="h-px w-full bg-gray-50"></div>

              {/* Card Body - Indicators */}
              <div className="p-6 pt-4 flex-1 flex flex-col">
                <h4 className="text-xs font-bold tracking-wider text-gray-400 mb-3 uppercase">
                  Key Indicators
                </h4>

                <div className="space-y-3 flex-1">
                  {displayIndicators.map((ind, idx) => (
                    <div
                      key={`${category.id}-ind-${idx}`}
                      className="flex items-start gap-3 group/item"
                    >
                      <div
                        className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${category.color} opacity-40 group-hover/item:opacity-100 transition-opacity`}
                      />
                      <span className="text-sm text-gray-600 line-clamp-1 group-hover/item:text-gray-900 transition-colors">
                        {ind}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Expansion Toggle */}
                {(category.sampleIndicators || []).length > 3 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(
                        expanded === category.id ? null : category.id,
                      );
                    }}
                    className="mt-4 text-xs font-medium text-trac-600 hover:text-trac-800 transition-colors self-start focus:outline-none"
                  >
                    {expanded === category.id
                      ? "Show less"
                      : `+ ${(category.sampleIndicators || []).length - 3} more indicators`}
                  </button>
                )}
              </div>

              {/* Action Footer */}
              <div
                className={`p-4 bg-gray-50 border-t border-gray-100 transition-colors duration-300 ${isSelected ? "bg-trac-50" : "group-hover:bg-trac-50/30"}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-semibold transition-colors ${isSelected ? "text-trac-700" : "text-gray-400 group-hover:text-trac-600"}`}
                  >
                    {isSelected ? "Currently Selected" : "Explore Data"}
                  </span>
                  <ArrowRightIcon
                    className={`w-5 h-5 transition-all duration-300 ${isSelected ? "text-trac-600 translate-x-0" : "text-gray-300 -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-trac-500"}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryGrid;
